import { useEffect, useMemo, useCallback } from 'react';

import {
  GetCreditsInterestFormResponse,
  FormUser,
  FormQuestions,
  FormQuestion,
  RelationToAmazon,
  RelationToAmazonStruct,
  UpsertCreditsInterestFormResponse,
} from '@amzn/coral_com-amazon-asxcustomerprofileservice';
import { useApi } from '@amzn/sss-website-theme';

import { useAcpsCreditsClient } from 'src/clients/acps';
import {
  formatOrganizationRelationship,
  OrganizationInformationFormValues,
  OrganizationCommitmentsFormValues,
  INTAKE_FORM_KEYS,
} from 'src/components/pages/intake-form/FormEnums';
import { IntakeFormQuestionInfo } from 'src/constants/panther/form-strings';
import { t } from 'src/helpers';

export interface OrganizationData {
  name: string;
  email: string;
  organizationName: string;
  organizationRelationship: string;
  countryCode: string;
}

export interface BuyerCriteriaData {
  question1: string;
  question2: string;
  subquestion1: string;
}

export interface CreditsInterestFormData {
  organizationData: OrganizationData;
  buyerCriteriaData: BuyerCriteriaData;
  isLoading: boolean;
  hasCallCompleted: boolean;
  error: Error | null;
  upsertCreditsInterest: (
    organizationData?: OrganizationInformationFormValues,
    commitmentsData?: OrganizationCommitmentsFormValues
  ) => Promise<UpsertCreditsInterestFormResponse>;
}

// Helper function to safely get question response by index
const getQuestionResponse = (questions: FormQuestions | undefined, index: number): string => {
  if (!questions || !questions[index]) {
    return '';
  }
  return questions[index].questionResponse ?? '';
};

// Helper function to safely get subquestion response
const getSubquestionResponse = (
  questions: FormQuestions | undefined,
  questionIndex: number,
  subquestionIndex: number = 0
): string => {
  if (!questions || !questions[questionIndex] || !questions[questionIndex].subquestions) {
    return '';
  }
  const { subquestions } = questions[questionIndex];
  if (!subquestions || !subquestions[subquestionIndex]) {
    return '';
  }
  return subquestions[subquestionIndex].questionResponse ?? '';
};

// Map FormUser to organization data
const mapToOrganizationData = (user: FormUser | undefined, intakeFormBundle: any): OrganizationData => {
  if (!user) {
    return {
      name: '',
      email: '',
      organizationName: '',
      organizationRelationship: '',
      countryCode: '',
    };
  }

  // Extract the RelationToAmazon array from the struct and convert strings to enum values
  const relationList = user.relationToAmazon?.selectedRelationsToAmazon;
  const relationArray = relationList
    ? (relationList as string[]).map((relation) => relation as RelationToAmazon)
    : undefined;

  return {
    name: user.name ?? '',
    email: user.email ?? '',
    organizationName: user.company ?? '',
    organizationRelationship: formatOrganizationRelationship(relationArray, intakeFormBundle),
    countryCode: user.countryCode ?? '',
  };
};

// Map FormQuestions to buyer criteria data
const mapToBuyerCriteriaData = (questions: FormQuestions | undefined): BuyerCriteriaData => ({
  question1: getQuestionResponse(questions, 0),
  question2: getQuestionResponse(questions, 1),
  subquestion1: getSubquestionResponse(questions, 0, 0),
});

// Transform organization form data to API FormUser format
const organizationFormToApiUser = (formData: OrganizationInformationFormValues): FormUser =>
  FormUser.clone({
    name: formData[INTAKE_FORM_KEYS.NAME] ?? '',
    email: formData[INTAKE_FORM_KEYS.EMAIL] ?? '',
    company: formData[INTAKE_FORM_KEYS.ORGANIZATION_NAME] ?? '',
    relationToAmazon: RelationToAmazonStruct.clone({
      selectedRelationsToAmazon: formData[INTAKE_FORM_KEYS.ORGANIZATION_RELATIONSHIP] ?? [],
    }),
    countryCode: formData[INTAKE_FORM_KEYS.COUNTRY_CODE] ?? '',
  });

// Transform commitments form data to API FormQuestions format
const commitmentsFormToApiQuestions = (
  formData: OrganizationCommitmentsFormValues,
  intakeFormBundle?: any
): FormQuestions => [
  FormQuestion.clone({
    questionId: IntakeFormQuestionInfo.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION.id,
    questionText: intakeFormBundle
      ? t(intakeFormBundle, IntakeFormQuestionInfo.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION.text)
      : '',
    questionResponse: formData[INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION] ?? '',
    ...(formData[INTAKE_FORM_KEYS.SUBQUESTION_1] && {
      subquestions: [
        FormQuestion.clone({
          questionId: IntakeFormQuestionInfo.SUBQUESTION_1.id,
          questionText: intakeFormBundle ? t(intakeFormBundle, IntakeFormQuestionInfo.SUBQUESTION_1.text) : '',
          questionResponse: formData[INTAKE_FORM_KEYS.SUBQUESTION_1] ?? '',
        }),
      ],
    }),
  }),
  FormQuestion.clone({
    questionId: IntakeFormQuestionInfo.MEASURE_AND_REPORT_CRITERIA_QUESTION.id,
    questionText: intakeFormBundle
      ? t(intakeFormBundle, IntakeFormQuestionInfo.MEASURE_AND_REPORT_CRITERIA_QUESTION.text)
      : '',
    questionResponse: formData[INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION] ?? '',
  }),
];

export const useCreditsInterestFormData = (intakeFormBundle?: any): CreditsInterestFormData => {
  const { getCreditsInterestForm, upsertCreditsInterestForm } = useAcpsCreditsClient();

  const {
    isLoading,
    hasCallCompleted,
    response: apiResponse,
    error,
    callApi: fetchCreditsInterestForm,
  } = useApi<GetCreditsInterestFormResponse>();

  // Fetch data on component mount
  useEffect(() => {
    fetchCreditsInterestForm(getCreditsInterestForm);
  }, [fetchCreditsInterestForm, getCreditsInterestForm]);

  // Memoize the mapped data to avoid unnecessary recalculations
  const mappedData = useMemo(() => {
    const organizationData = mapToOrganizationData(apiResponse?.user, intakeFormBundle);
    const buyerCriteriaData = mapToBuyerCriteriaData(apiResponse?.questions);

    return {
      organizationData,
      buyerCriteriaData,
    };
  }, [apiResponse, intakeFormBundle]);

  // Upsert function to save form data to API
  const upsertCreditsInterest = useCallback(
    async (
      organizationData?: OrganizationInformationFormValues,
      commitmentsData?: OrganizationCommitmentsFormValues
    ): Promise<UpsertCreditsInterestFormResponse> => {
      // Use provided data or fall back to existing API data
      const user = organizationData ? organizationFormToApiUser(organizationData) : apiResponse?.user;

      const questions = commitmentsData
        ? commitmentsFormToApiQuestions(commitmentsData, intakeFormBundle)
        : apiResponse?.questions;

      if (!user || !questions) {
        throw new Error('Missing required user or questions data for upsert operation');
      }

      return upsertCreditsInterestForm(user, questions);
    },
    [upsertCreditsInterestForm, apiResponse, intakeFormBundle]
  );

  return {
    ...mappedData,
    isLoading,
    hasCallCompleted: hasCallCompleted ?? false,
    error: error || null,
    upsertCreditsInterest,
  };
};
